/* ═════════════════════════════════════════════════════════════
   kc-filter.js — client-side search, filtering and paging for every
   Knowledge Centre grid: the Plant Guide, the disease and pest guides,
   and the video library.

   WHY CLIENT-SIDE. Liquid cannot filter metaobjects server-side, and
   filtering only the current page would report counts that are wrong.
   The server therefore renders the complete set (paginate ... by 250)
   and this module narrows it.

   PROGRESSIVE ENHANCEMENT. The controls are markup-hidden until this
   file runs, so a visitor without JavaScript is never shown a search box
   that cannot search — they get the full list instead, which is complete
   and correct, just longer. Nothing here fetches or invents data; it only
   hides and shows what Liquid already printed.

   CONTRACT — one root per grid:
     [data-kc-filter]              root, optional data-per-page, data-lang="ar|en"
       [data-kc-controls]          revealed on init
       [data-kc-q]                 free-text input, matched against card data-q
       [data-kc-f="cat"]           <select>; compares card dataset.cat
       [data-kc-reset]             clears everything
       [data-kc-count]             live region for the result count
       [data-kc-grid] > [data-kc-item]
       [data-kc-none]              shown when nothing matches
       [data-kc-pager]             built here; empty in the markup
   Any number of [data-kc-f] keys is supported — adding a filter is a
   markup change, never a change to this file.
   ═════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  /* Arabic is written with several interchangeable letterforms. Someone typing
     "اراك" should still find "أراك", and "نخله" should find "نخلة". This folds
     for MATCHING only — it never alters what is displayed. */
  function norm(s) {
    return (s || '').toString().toLowerCase()
      .replace(/[أإآٱ]/g, 'ا')
      .replace(/ة/g, 'ه')
      .replace(/[ىي]/g, 'ي')
      .replace(/[ً-ْٰ]/g, '')
      .replace(/ـ/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function init(root) {
    var perPage  = parseInt(root.dataset.perPage, 10) || 24;
    var isAr     = root.dataset.lang === 'ar';
    var controls = root.querySelector('[data-kc-controls]');
    var input    = root.querySelector('[data-kc-q]');
    var selects  = Array.prototype.slice.call(root.querySelectorAll('[data-kc-f]'));
    var grid     = root.querySelector('[data-kc-grid]');
    var none     = root.querySelector('[data-kc-none]');
    var pager    = root.querySelector('[data-kc-pager]');
    var countEl  = root.querySelector('[data-kc-count]');
    if (!grid) return;

    var items = Array.prototype.slice.call(grid.querySelectorAll('[data-kc-item]'));
    var page = 1;
    var matched = items;

    if (controls) controls.removeAttribute('hidden');
    items.forEach(function (el) { el.dataset.norm = norm(el.dataset.q); });

    function readParams() {
      var p = new URLSearchParams(window.location.search);
      if (input) input.value = p.get('q') || '';
      selects.forEach(function (s) { s.value = p.get(s.dataset.kcF) || ''; });
      page = parseInt(p.get('page'), 10) || 1;
    }

    function writeParams() {
      var p = new URLSearchParams(window.location.search);
      /* Only this module's own keys are rewritten, so an unrelated query
         parameter on the URL survives a filter change. */
      p.delete('q'); p.delete('page');
      selects.forEach(function (s) { p.delete(s.dataset.kcF); });
      if (input && input.value.trim()) p.set('q', input.value.trim());
      selects.forEach(function (s) { if (s.value) p.set(s.dataset.kcF, s.value); });
      if (page > 1) p.set('page', page);
      var qs = p.toString();
      history.replaceState(null, '', qs ? '?' + qs : window.location.pathname);
    }

    function filter() {
      var term = norm(input ? input.value : '');
      matched = items.filter(function (el) {
        if (term && el.dataset.norm.indexOf(term) === -1) return false;
        for (var i = 0; i < selects.length; i++) {
          var key = selects[i].dataset.kcF;
          var want = selects[i].value;
          if (want && el.dataset[key] !== want) return false;
        }
        return true;
      });
    }

    function render() {
      var pages = Math.max(1, Math.ceil(matched.length / perPage));
      if (page > pages) page = pages;
      if (page < 1) page = 1;

      var from = (page - 1) * perPage;
      items.forEach(function (el) { el.hidden = true; });
      matched.slice(from, from + perPage).forEach(function (el) { el.hidden = false; });

      if (none) none.hidden = matched.length !== 0;
      if (countEl) {
        countEl.textContent = isAr
          ? matched.length + ' من ' + items.length
          : matched.length + ' of ' + items.length;
      }
      buildPager(pages);
    }

    function go(target) {
      page = target;
      writeParams();
      render();
      grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
      /* Move focus to the new results so keyboard and screen-reader users are
         not left on a control that has just been rebuilt underneath them. */
      grid.setAttribute('tabindex', '-1');
      grid.focus({ preventScroll: true });
    }

    function buildPager(pages) {
      if (!pager) return;
      pager.textContent = '';
      if (pages <= 1) { pager.hidden = true; return; }
      pager.hidden = false;

      function btn(label, target, current, aria) {
        /* A <button>, not a link to "#": these change what this page shows
           rather than navigating, and a fake link announces itself wrongly
           and breaks middle-click and open-in-new-tab. */
        var el = document.createElement(current ? 'span' : 'button');
        el.textContent = label;
        if (current) {
          el.className = 'is-current';
          el.setAttribute('aria-current', 'page');
        } else {
          el.type = 'button';
          if (aria) el.setAttribute('aria-label', aria);
          el.addEventListener('click', function () { go(target); });
        }
        pager.appendChild(el);
      }
      function gap() {
        var s = document.createElement('span');
        s.className = 'kpag__gap';
        s.setAttribute('aria-hidden', 'true');
        s.textContent = '…';
        pager.appendChild(s);
      }

      var pageWord = isAr ? 'صفحة ' : 'Page ';
      if (page > 1) btn(isAr ? 'السابق' : 'Previous', page - 1, false);
      /* A window around the current page, so 200 records do not print 9 links. */
      var start = Math.max(1, page - 2);
      var end   = Math.min(pages, page + 2);
      if (start > 1) btn('1', 1, false, pageWord + '1');
      if (start > 2) gap();
      for (var i = start; i <= end; i++) btn(String(i), i, i === page, pageWord + i);
      if (end < pages - 1) gap();
      if (end < pages) btn(String(pages), pages, false, pageWord + pages);
      if (page < pages) btn(isAr ? 'التالي' : 'Next', page + 1, false);
    }

    function onInput() {
      page = 1;
      writeParams();
      filter();
      render();
    }

    var timer;
    if (input) {
      input.addEventListener('input', function () {
        clearTimeout(timer);
        timer = setTimeout(onInput, 160);
      });
    }
    selects.forEach(function (s) { s.addEventListener('change', onInput); });

    var reset = root.querySelector('[data-kc-reset]');
    if (reset) {
      reset.addEventListener('click', function () {
        if (input) input.value = '';
        selects.forEach(function (s) { s.value = ''; });
        onInput();
        if (input) input.focus();
      });
    }

    readParams();
    filter();
    render();
  }

  function boot() {
    document.querySelectorAll('[data-kc-filter]').forEach(init);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
